import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './modules/app-routing.module';
import { AppComponent } from './app.component';
import { AsyncPipe, CommonModule, DatePipe, NgFor } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { PrimengModule } from "./modules/primeng.module";
import { MainComponent } from './components/main/main.component';
import { ResultsComponent } from './components/results/results.component';
import { AdvancedSearchComponent } from './components/search/search-advanced/search-advanced.component';
import { DialogNotificationsComponent } from "./components/notifications/dialog-notifications.component";
import { SearchService } from './services/search.service';
import { SearchHeadComponent } from './components/search/search-head/search-head.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { ResultsBodyListComponent } from './components/results/results-body/results-body-list/results-body-list.component';
import { ResultsBodyGridComponent } from './components/results/results-body/results-body-grid/results-body-grid.component';
import { ResultsBodyTableComponent } from './components/results/results-body/results-body-table/results-body-table.component';
import { ResultsBodyMapComponent } from './components/results/results-body/results-body-map/results-body-map.component';
import { ExportExcelComponent } from './components/export-excel/export-excel.component';
import { FootComponent } from './components/foot/foot.component';
import { GridCardComponent } from './components/grid-card/grid-card.component';
import { SafePipe } from './pipes/safe.pipe';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { TabMenuModule } from "primeng/tabmenu";
import { MenuModule } from "primeng/menu";
import { PanelModule } from 'primeng/panel';
import { InputSwitchModule } from 'primeng/inputswitch';
import { PeriodDialogComponent } from './components/search/search-dialogs/period-dialog/period-dialog.component';
import { LicenseDialogComponent } from './components/search/search-dialogs/license-dialog/license-dialog.component';
import { SearchTreeComponent } from './components/search/search-tree/search-tree.component';
import { ThesaurusSearchTreeComponent } from './components/search/thesaurus-search-tree/thesaurus-search-tree.component';
import { FilterTagsComponent } from './components/search/filter-tags/filter-tags.component';
import { ThesaurusFilterTagsComponent } from './components/search/thesaurus-filter-tags/thesaurus-filter-tags.component';
import { SearchTypeSelectorComponent } from './components/search/search-type-selector/search-type-selector.component';
import { LocusDetailsComponent } from './components/locus/locus-details/locus-details.component';
import { LocusHeadComponent } from './components/locus/locus-head/locus-head.component';
import { LocusComponent } from './components/locus/locus.component';
import { LocusBasketListComponent } from './components/locus/locus-basket/locus-basket-list/locus-basket-list.component';
import { LocusBasketItemComponent } from './components/locus/locus-basket/locus-basket-item/locus-basket-item.component';
import { ObjectComponent } from './components/object/object.component';
import { CoinComponent } from './components/coin/coin.component';
import { VersionInfoComponent } from './components/version-info/version-info.component';
import { LocusBasketGroupsComponent } from './components/locus/locus-basket/locus-basket-groups/locus-basket-groups.component';
import { SearchDialogComponent } from './components/search/search-dialog/search-dialog.component';
import { UnauthorizedDialogComponent } from './components/results/unauthorized-dialog/unauthorized-dialog.component';
import { OrdinaryObjectRowComponent } from './components/object/ordinary-object-row/ordinary-object-row.component';
import { LocusItemComponent } from './components/locus/locus-item/locus-item.component';
import { HachrazaComponent } from './components/hachraza/hachraza.component';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { FieldsetModule } from 'primeng/fieldset';
import { MsalModule, MsalService, MSAL_INSTANCE, MsalGuard, MsalRedirectComponent, MsalInterceptor, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService } from '@azure/msal-angular';
import {  InteractionType, IPublicClientApplication } from '@azure/msal-browser';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';

import { AboutComponent } from './components/about/about.component';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { ScientificArchiveDocumentModule } from './components/scientific-archive-document/scientific-archive-document.module';
import { ConservationArchiveDocumentModule } from './components/conservation-archive-document/conservation-archive-document.module';

import { ImageGalleryModule } from './components/image-gallery/image-gallery.module';
import { AuthService } from './services/auth.service';
import { CustomMsalInterceptor } from './interceptors/custom-msal.interceptor';
import { EntityPageModule } from './components/entity-page/entity-page.module';
import { EntityPageComponent } from './components/entity-page/entity-page.component';
import { PlasterSampleComponent } from './components/plaster-sample/plaster-sample.component';
import { PlasterSampleDetailsComponent } from './components/plaster-sample/plaster-sample-details/plaster-sample-details.component';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ScientificArchiveFolderModule } from "./components/scientific-archive-folder/scientific-archive-folder.module";
import { ConservationArchiveFolderModule } from './components/conservation-archive-folder/conservation-archive-folder.module';
import { HbSectionModule } from './components/hb-section/hb-section.module';
import { AriaLabelTargetDirective } from './directives/aria-label.directive';
import { DialogTableAccessibilityDirective } from './directives/dialog-table-accessibility-directive';
import { ShowMoreDialogComponent } from './components/show-more-dialog/show-more-dialog.component';
import { ArcGisMapComponent } from './components/arcgis-map/arcgis-map.component';
import { AccessibilityComponent } from './components/accessibility/accessibility.component';
import { TermsComponent } from './components/terms/terms.component';
import { Router } from '@angular/router';
import { TermsEnComponent } from './components/terms/term-en/terms-en.component';
import { ContactUsEnComponent } from './components/contact-us/contact-us-en/contact-us-en.component';
import { DeferBlockBehavior } from '@angular/core/testing';
import { catchError, of } from 'rxjs';
import { SharedPipeModule } from './components/shared/shared.module';
import { LanguageService } from './services/language.service';
import { ResultsBodyFootComponent } from './components/results/results-body/results-body-foot/results-body-foot.component';
import { MSALInstanceService } from './services/msal-instance.service';
import { BrToNewLinePipe } from "src/app/pipes/brToNewLine";

import { ModuleHeaderModule } from './components/module-header/module-header.module';
// import { ModuleHeaderTestModule } from './components/module-header/module-header.module';
import { DialogModule } from 'primeng/dialog';
import { DialogService } from 'primeng/dynamicdialog';
import { ResultsMessageComponent } from './components/results-message/results-message.component';
import { TranslateLangPipe } from './components/shared/pipes/translateLang.pipe';
import { IIIFService } from './services/iiif.service';
import { TestIIIFComponent } from './components/iiif-test/iiif-test.component';
import { IIIFTiledViewerComponent } from './components/iiif-viewer/iiif-tiled-viewer.component';
import { FragmentComponent } from './components/fragment/fragment.component';
import { TextFragmentComponent } from './components/text-fragment/text-fragment.component';
import { ManuscriptComponent } from "./components/manuscript/manuscript.component";
import { ManuscriptDetailsComponent } from "./components/manuscript/manuscript-details/manuscript-details.component";
import { ReturnFocusDirective } from "./components/shared/directives/return-focus.directive";
import { ExcavationComponent } from "./components/excavation/excavation.component";
import { ExcavationDetailsComponent } from "./components/excavation/excavation-details/excavation-details.component";
import { SurveyComponent } from "./components/survey/survey.component";
import { SurveyDetailsComponent } from "./components/survey/survey-details/survey-details.component";
import { AuthenticationNewComponent } from './components/authentication_new/authentication_new.component';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { noCacheInterceptor } from './interceptors/no-cache.interceptor';
import { NlSearchChatComponent } from './components/nl-search-chat/nl-search-chat.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PdfViewerDialogComponent } from './components/shared/pdf-viewer-dialog/pdf-viewer-dialog.component';
import { ModelComponent } from './components/model/model.component';

export function HttpLoaderFactory(http: HttpClient, t:TranslateService) {
  return new TranslateHttpLoader(http,  './assets/locale/', '.json');
}
export function MSALInstanceProvider(msalInstanceService: MSALInstanceService): IPublicClientApplication {
    return msalInstanceService.getInstanceOrStub();
}
export function initializeAuth(msalInstanceService: MSALInstanceService): () => Promise<void> {
    return async () => {
      if (msalInstanceService.getCurrentAuthType() === 'guest') {
        return;
      }
      const instance = msalInstanceService.getInstance();
      await instance.initialize();
      await instance.handleRedirectPromise();
    };
  }
@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [         BrToNewLinePipe
,
    AuthenticationNewComponent,
    TextFragmentComponent,
    FragmentComponent,
    IIIFTiledViewerComponent,
    TestIIIFComponent,
    UnauthorizedComponent,
    AppComponent,
    MainComponent,
    AdvancedSearchComponent,
    ResultsComponent,
    DialogNotificationsComponent,
    SearchHeadComponent,
    TabsComponent,
    ResultsBodyFootComponent,
    ResultsBodyListComponent,
    ResultsBodyGridComponent,
    ResultsBodyTableComponent,
    ExportExcelComponent,
    FootComponent,
    GridCardComponent,
    SafePipe,
    PeriodDialogComponent,
    LicenseDialogComponent,
    SearchTreeComponent,
    ThesaurusSearchTreeComponent,
    FilterTagsComponent,
    ThesaurusFilterTagsComponent,
    SearchTypeSelectorComponent,
    LocusHeadComponent,
    LocusItemComponent,
    ObjectComponent,
    CoinComponent,
    OrdinaryObjectRowComponent,
    VersionInfoComponent,
    SearchDialogComponent,
    UnauthorizedDialogComponent,
    HachrazaComponent,
    AboutComponent,
    AccessibilityComponent,
    TermsComponent,
    TermsEnComponent,
    ContactUsComponent,
    AriaLabelTargetDirective,
    DialogTableAccessibilityDirective,
    ContactUsEnComponent,
    ResultsMessageComponent,
    ManuscriptComponent,
    ManuscriptDetailsComponent,
    ExcavationComponent,
    ExcavationDetailsComponent,
    SurveyComponent,
    SurveyDetailsComponent,
    LocusComponent,
    LocusBasketItemComponent,
    LocusBasketListComponent,
    LocusBasketGroupsComponent,
    LocusDetailsComponent,
    ShowMoreDialogComponent,
    ResultsBodyMapComponent,
    NlSearchChatComponent,
    PdfViewerDialogComponent,
    ModelComponent,
    PlasterSampleComponent,
    PlasterSampleDetailsComponent
  ],
  imports: [
    MsalModule.forRoot(null as unknown as IPublicClientApplication, {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: []
        }
    }, {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map()
    }),
    TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpClient],
        },
    }),
    NgFor,
    AsyncPipe,
    ReturnFocusDirective,
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    PrimengModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    TabMenuModule,
    MenuModule,
    PanelModule,
    InputSwitchModule,
    NgxGoogleAnalyticsModule.forRoot((window as any)?.ga),
    NgxGoogleAnalyticsRouterModule,
    FieldsetModule,
    ScientificArchiveDocumentModule,
    ConservationArchiveDocumentModule,

    ImageGalleryModule,
    ScientificArchiveFolderModule,
    ConservationArchiveFolderModule,
    HbSectionModule,
    EntityPageModule,
    SharedPipeModule,
    ModuleHeaderModule,
    // ModuleHeaderTestModule,
    DialogModule,
    ArcGisMapComponent,
    NgxExtendedPdfViewerModule
],
  providers: [
    provideHttpClient(
      withInterceptors([noCacheInterceptor])
    ),
    IIIFService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuth,
      deps: [MSALInstanceService],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceProvider,
      deps: [MSALInstanceService]
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomMsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: (msalInstanceService: MSALInstanceService) => ({
        interactionType: InteractionType.Redirect,
        protectedResourceMap: msalInstanceService.getProtectedResourceMap()
      }),
      deps: [MSALInstanceService]
    },
    TranslateLangPipe,
    SearchService,
    DatePipe,
    MsalService,
    MsalGuard,
    AuthService,
    LanguageService,
    MsalBroadcastService,
    DialogService
  ],
  bootstrap: [AppComponent ],
})
export class AppModule { }
